import * as React from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import { Card, CardContent, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { CarrierTable } from './CarrierTable';
import { formatChipBgColorAvaliability, formatChipFontColorAvaliability } from 'utils/AppUtil';



function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <Box
            role="tabpanel"
            hidden={value !== index}
            id={`vertical-tabpanel-${index}`}
            aria-labelledby={`vertical-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </Box>
    );
}

function a11yProps(index) {
    return {
        id: `vertical-tab-${index}`,
        'aria-controls': `vertical-tabpanel-${index}`,
    };
}

const DataUsageCard = styled(Card)({
    backgroundColor: '#FFFFFF',
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
    marginBottom: '10px',
    '& .MuiCardContent-root': {
        padding: '1px',
    },
    '& .MuiCardContent-root:last-child': {
        paddingBottom: 0
    }
});


export const VerticalTabs = ({
    tableData,
    dateSelectionMode,
    setSelectedVerticleTab,
    selectedVerticleTab,
    sitePerf,
    selectedDates,
    volteCallsCount,
    comparisonEnabled,
    comparisonDates,
    comparisonTableData,
    comparisonVolteCallsCount,
    isLoadingComparisonData,
    comparisonDataError,
}) => {
    const [value, setValue] = React.useState(selectedVerticleTab);
    const [comparisonView, setComparisonView] = React.useState('table');

    React.useEffect(() => {
        if (!comparisonEnabled) {
            setComparisonView('table');
        }
    }, [comparisonEnabled]);

    const handleChange = (event, newValue) => {
        setValue(newValue);
        setSelectedVerticleTab(newValue);
    };

    const hasComparisonRange = Boolean(
        comparisonEnabled && comparisonDates?.start?._d && comparisonDates?.end?._d
    );

    const renderCarrierContent = (universityName) => {
        if (!hasComparisonRange) {
            return (
                <CarrierTable
                    universityName={universityName}
                    tableData={tableData}
                    dateSelectionMode={dateSelectionMode}
                    sitePerf={sitePerf}
                    selectedDates={selectedDates}
                    volteCallsCount={volteCallsCount}
                />
            );
        }

        return (
            <CarrierTable
                universityName={universityName}
                tableData={tableData}
                dateSelectionMode="range"
                sitePerf={sitePerf}
                selectedDates={selectedDates}
                volteCallsCount={volteCallsCount}
                currentView={comparisonView}
                onViewChange={setComparisonView}
                comparisonDateRanges={{ first: selectedDates, second: comparisonDates }}
                comparisonTableData={comparisonTableData}
                comparisonVolteCallsCount={comparisonVolteCallsCount}
                isLoadingComparisonData={isLoadingComparisonData}
                comparisonDataError={comparisonDataError}
            />
        );
    };

    return (
        <Box sx={{ padding: { xs: "0px", sm: "0px", md: "16px", lg: "16px", xl: "16px" } }}>
            <DataUsageCard>
                <CardContent sx={{ padding: { xs: "0px", sm: "0px" } }}>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' }
                    }}>
                        <Tabs
                            orientation="vertical"
                            variant="scrollable"
                            value={value}
                            onChange={handleChange}
                            aria-label="Vertical tabs example"
                            sx={{
                                width: { xs: '100%', md: '14rem' },
                                borderRight: '1px solid rgba(0, 0, 0, 0.12)',
                                borderBottom: { xs: '1px solid rgba(0, 0, 0, 0.12)', md: 0 },
                                borderColor: 'divider',
                                flexDirection: { xs: 'row', md: 'column' },
                                '& .MuiTabs-flexContainer': {
                                    flexDirection: { xs: 'row', md: 'column' },
                                },
                                '& .MuiTab-root': {
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    textAlign: 'center',
                                    textTransform: 'none',
                                    fontFamily: "Montserrat",
                                    fontSize: "14px",
                                    fontWeight: "500",
                                    color: '#005AA6',
                                    flex: { xs: 1, md: 'unset' },
                                    padding: { xs: "8px", sm: "8px", md: "8px", lg: "12px", xl: "12px" },
                                    minWidth: { xs: 'auto', md: 'unset' },
                                },
                                '& .Mui-selected': {
                                    color: '#0A2240 !important',
                                    fontWeight: "700",
                                    fontFamily: "Montserrat",
                                    borderBottom: { xs: '1px solid #0A2240', md: 0 },
                                },
                                '& .MuiTabs-indicator': {
                                    display: { xs: 'none', md: 'block' },
                                    backgroundColor: '#0A2240',
                                },
                            }}
                        >
                            <Tab label={
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: { xs: "center", sm: "center", md: "space-between", lg: "space-between", xl: "space-between" },
                                    gap: 1,
                                    width: '100%',
                                    flexWrap: 'nowrap',
                                    overflow: 'hidden'
                                }}>
                                    <span style={{
                                        whiteSpace: 'nowrap',
                                        fontSize: '14px',
                                        flex: { md: 1 },           // ← Takes full space on desktop
                                        textAlign: { md: 'left' }  // ← Left align on desktop
                                    }}>Syracuse University</span>
                                    {sitePerf['syracyse'] && <Chip
                                        label={sitePerf['syracyse'] > 0
                                            ? `${sitePerf['syracyse']}%`
                                            : `${sitePerf['syracyse']}%`}
                                        size="small"
                                        sx={{
                                            display: { xs: 'none', md: 'inline-flex' },
                                            minWidth: '48px',
                                            height: '24px',
                                            fontSize: '12px',
                                            fontWeight: 600,
                                            flexShrink: 0,
                                            bgcolor: formatChipBgColorAvaliability(sitePerf['syracyse']),
                                            color: formatChipFontColorAvaliability(sitePerf['syracyse'])
                                        }}
                                    />}
                                </Box>
                            } {...a11yProps(0)} />
                            <Tab label={
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: { xs: "center", sm: "center", md: "space-between", lg: "space-between", xl: "space-between" },
                                    gap: 1,
                                    width: '100%',
                                    flexWrap: 'nowrap',
                                    overflow: 'hidden',
                                }}>
                                    <span style={{
                                        whiteSpace: 'nowrap',
                                        fontSize: '14px',
                                        flex: { md: 1 },           // ← Takes full space on desktop
                                        textAlign: { md: 'left' }  // ← Left align on desktop
                                    }}>Caesars Nobu</span>
                                    {sitePerf['caesars'] && <Chip
                                        label={sitePerf['caesars'] > 0
                                            ? `${sitePerf['caesars']}%`
                                            : `${sitePerf['caesars']}%`}
                                        size="small"
                                        sx={{
                                            display: { xs: 'none', md: 'inline-flex' },
                                            minWidth: '48px',
                                            height: '24px',
                                            fontSize: '12px',
                                            fontWeight: 600,
                                            flexShrink: 0,
                                            bgcolor: formatChipBgColorAvaliability(sitePerf['caesars']),
                                            color: formatChipFontColorAvaliability(sitePerf['caesars'])
                                        }}
                                    />}
                                </Box>
                            } {...a11yProps(1)} />
                            <Tab label={
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: { xs: "center", sm: "center", md: "space-between", lg: "space-between", xl: "space-between" },
                                    gap: 1,
                                    width: '100%',
                                    flexWrap: 'nowrap',
                                    overflow: 'hidden',
                                }}>
                                    <span style={{
                                        whiteSpace: 'nowrap',
                                        fontSize: '14px',
                                        flex: { md: 1 },           // ← Takes full space on desktop
                                        textAlign: { md: 'left' }  // ← Left align on desktop
                                    }}>JMA Headquarters</span>
                                    {sitePerf['jmahq'] && <Chip
                                        label={sitePerf['jmahq'] > 0
                                            ? `${sitePerf['jmahq']}%`
                                            : `${sitePerf['jmahq']}%`}
                                        size="small"
                                        sx={{
                                            display: { xs: 'none', md: 'inline-flex' },
                                            minWidth: '48px',
                                            height: '24px',
                                            fontSize: '12px',
                                            fontWeight: 600,
                                            flexShrink: 0,
                                            bgcolor: formatChipBgColorAvaliability(sitePerf['jmahq']),
                                            color: formatChipFontColorAvaliability(sitePerf['jmahq'])
                                        }}
                                    />}
                                </Box>
                            } {...a11yProps(2)} />
                        </Tabs>
                        <Box sx={{
                            flex: 1,
                            overflowX: 'auto',
                            overflowY: 'hidden',
                            minWidth: 0,
                            '& > div': {
                                minWidth: { xs: 'auto', sm: 'auto' }
                            },
                            "& .css-19kzrtu": {
                                padding: { xs: "5px", sm: "5px", md: "24px", lg: "24px", xl: "24px" }
                            }

                        }}>
                            <TabPanel value={value} index={0}>
                                {renderCarrierContent("syracyse")}
                            </TabPanel>
                            <TabPanel value={value} index={1}>
                                {renderCarrierContent("caesars")}
                            </TabPanel>
                            <TabPanel value={value} index={2}>
                                {renderCarrierContent("jmahq")}
                            </TabPanel>
                        </Box>
                    </Box>
                </CardContent>
            </DataUsageCard>
        </Box>
    );
};

export default VerticalTabs;